import styled, { css } from 'styled-components';
import { memo } from 'react';
import dynamic from 'next/dynamic';

import { DESKTOP_HEADER_HEIGHT } from 'layout/header/enums/constants';
import {
  SUPER_COMPRESSED_DESKTOP_HEADER_HEIGHT,
  TIMELINE_NAVIGATION_DESKTOP_HEIGHT,
} from 'search/styles/SearchStyles';
import { IConstructorFacet } from 'shared/types/ConstructorFacet';
import { IConstructorGroup } from 'shared/types/ConstructorGroup';
import { IConstructorSortOption } from 'shared/types/ConstructorSortOption';
import { ISubCollectionList } from 'shared/types/ConsumerSearch';
import SearchFilterPriceDesktop from 'search/components/searchGrid/SearchFilterPriceDesktop';
import SearchSortDesktop from 'search/components/searchGrid/SearchSortDesktop';
import { SearchFilterDesktop } from 'search/components/searchGrid/SearchFilterDesktop';
import { SearchFilterDiscountAndAvailableNow } from 'search/components/searchGrid/SearchFilterDiscountAndAvailableNow';
import { SearchFilterInstantShip } from 'search/components/searchGrid/SearchFilterInstantShip';
import SearchFilterSubCollectionDesktop from 'search/components/searchGrid/SearchFilterSubCollectionDesktop';
import { useShowFilterState } from 'search/hooks/useShowFilterState';
import {
  getAvailableNowFacet,
  getBrandFacet,
  getCategoryFacet,
  getColorFacet,
  getConditionFacet,
  getDiscountTagFacet,
  getGenderFacet,
  getInstantShipFacet,
  getPriceFacet,
  getSizeFacet,
  getYearFacet,
} from 'search/utils/getFacets';
import { hideScrollbars } from 'styles/utils/hideScrollbars';

const SearchClearFilters = dynamic(
  () => import('search/components/searchGrid/SearchClearFilters'),
  { ssr: false },
);

interface ISearchSortAndFiltersDesktop {
  facets: Array<IConstructorFacet>;
  groups: IConstructorGroup;
  hideMetadata?: boolean;
  isCrawler?: boolean;
  isStickyAndSuperCompressed?: boolean; // user has scrolled more from sticky point, and now it has a more collapse sticky setting
  isTimelinePath?: boolean;
  resultIds: Array<string>;
  sortOptions: Array<IConstructorSortOption>;
  subCollections?: ISubCollectionList;
}

export const SearchSortAndFiltersDesktop = ({
  facets,
  groups,
  hideMetadata,
  isStickyAndSuperCompressed,
  isTimelinePath,
  resultIds,
  sortOptions,
  isCrawler = false,
  subCollections,
}: ISearchSortAndFiltersDesktop) => {
  const {
    showAvailableNowFilter,
    showBrandFilter,
    showCategoryFilter,
    showColorFilter,
    showConditionFilter,
    showDiscountTagFilter,
    showGenderFilter,
    showInstantFilter,
    showPriceFilter,
    showSizeFilter,
    showYearFilter,
  } = useShowFilterState(isCrawler);
  const topLevelCategory = groups?.group_id;
  const hasNestedCategories = groups?.children?.length > 0;
  const availableNowFacet = showAvailableNowFilter && getAvailableNowFacet(facets);
  const brandFacet = showBrandFilter && getBrandFacet(facets);
  const categoryFacet = showCategoryFilter && hasNestedCategories && getCategoryFacet(facets);
  const colorFacet = showColorFilter && getColorFacet(facets);
  const conditionFacet = showConditionFilter && getConditionFacet(facets);
  const discountTagFacet = showDiscountTagFilter && getDiscountTagFacet(facets);
  const genderFacet = showGenderFilter && getGenderFacet(facets);
  const instantShipFacet = showInstantFilter && getInstantShipFacet(facets);
  const priceFacet = showPriceFilter && getPriceFacet(facets);
  const sizeFacet = showSizeFilter && getSizeFacet(facets);
  const yearFacet = showYearFilter && getYearFacet(facets);

  return (
    <SearchSortAndFiltersDesktop.Wrapper
      id="desktop-filters"
      $hideMetadata={hideMetadata}
      $isStickyAndSuperCompressed={isStickyAndSuperCompressed}
      $isTimelinePath={isTimelinePath}
    >
      {!isCrawler && <SearchSortDesktop sortOptions={sortOptions} />}
      <SearchSortAndFiltersDesktop.ClearFiltersWrapper>
        <SearchClearFilters resultIds={resultIds} />
      </SearchSortAndFiltersDesktop.ClearFiltersWrapper>
      {subCollections?.length > 0 && (
        <SearchFilterSubCollectionDesktop subCollections={subCollections} />
      )}
      {brandFacet && (
        <SearchFilterDesktop
          facet={brandFacet}
          isFilterExpanded={isTimelinePath}
          resultIds={resultIds}
          scrollable={brandFacet?.options?.length > 11}
          isCrawler={isCrawler}
        />
      )}
      {categoryFacet && (
        <SearchFilterDesktop
          facet={categoryFacet}
          topLevelCategory={topLevelCategory}
          resultIds={resultIds}
          isCrawler={isCrawler}
        />
      )}
      {genderFacet && <SearchFilterDesktop facet={genderFacet} resultIds={resultIds} />}
      {sizeFacet && <SearchFilterDesktop facet={sizeFacet} resultIds={resultIds} />}
      {conditionFacet && <SearchFilterDesktop facet={conditionFacet} resultIds={resultIds} />}
      {colorFacet && <SearchFilterDesktop facet={colorFacet} resultIds={resultIds} />}
      {priceFacet && <SearchFilterPriceDesktop facet={priceFacet} resultIds={resultIds} />}
      {instantShipFacet && (
        <SearchFilterInstantShip facet={instantShipFacet} resultIds={resultIds} />
      )}
      {(discountTagFacet || availableNowFacet) && (
        <SearchFilterDiscountAndAvailableNow
          availableNowFacet={availableNowFacet}
          discountTagFacet={discountTagFacet}
          resultIds={resultIds}
        />
      )}
      {yearFacet && <SearchFilterDesktop facet={yearFacet} resultIds={resultIds} />}
    </SearchSortAndFiltersDesktop.Wrapper>
  );
};

SearchSortAndFiltersDesktop.Wrapper = styled.div<{
  $isStickyAndSuperCompressed: boolean;
  $isTimelinePath: boolean;
  $hideMetadata?: boolean;
}>`
  padding: 0 16px 30px;
  text-align: right;
  ${hideScrollbars};
  z-index: ${({ theme }) => theme.zIndex.searchSortAndFilters};

  transition: all 0.3s;
  transition-timing-function: ease-in;

  position: sticky;

  ${({ $isTimelinePath }) =>
    css`
      position: sticky;
      overflow-y: auto;
      top: calc(
        ${SUPER_COMPRESSED_DESKTOP_HEADER_HEIGHT} +
          ${$isTimelinePath ? TIMELINE_NAVIGATION_DESKTOP_HEIGHT : '0px'}
      );
      max-height: calc(
        100vh - ${SUPER_COMPRESSED_DESKTOP_HEADER_HEIGHT} +
          ${$isTimelinePath ? TIMELINE_NAVIGATION_DESKTOP_HEIGHT : '0px'}
      );
    `}

  ${({ $hideMetadata }) =>
    $hideMetadata &&
    css`
      top: ${DESKTOP_HEADER_HEIGHT};
      max-height: calc(100vh - ${SUPER_COMPRESSED_DESKTOP_HEADER_HEIGHT});
    `}
`;

SearchSortAndFiltersDesktop.ClearFiltersWrapper = styled.div`
  height: 14px;
  margin-bottom: 16px;
`;

const areEqual = (prevProps, nextProps) => {
  return (
    JSON.stringify(prevProps.facets) === JSON.stringify(nextProps.facets) &&
    JSON.stringify(prevProps.sortOptions) === JSON.stringify(nextProps.sortOptions) &&
    JSON.stringify(prevProps.groups) === JSON.stringify(nextProps.groups) &&
    prevProps.isTimelinePath === nextProps.isTimelinePath &&
    prevProps.isStickyAndSuperCompressed === nextProps.isStickyAndSuperCompressed &&
    prevProps.subCollections === nextProps.subCollections
  );
};

export default memo(SearchSortAndFiltersDesktop, areEqual);
